import { parse } from 'date-fns';
import * as Yup from 'yup';

import { Option } from '../../design-system/types';
import { MarketCode } from '../dossier/types';

import { currencies, currenciesByMarket } from './constants';
import { InstalmentViewModel, ProofDocument, Traveler } from './types';

export const travelerStepValidationSchema = Yup.object().shape({
    travelers: Yup.array()
        .min(1)
        .of(
            Yup.object({
                firstName: Yup.string().required(
                    'contract.travelers_information.errors.first_name',
                ),
                lastName: Yup.string().required('contract.travelers_information.errors.last_name'),
                birthDate: Yup.date()
                    .required('contract.travelers_information.errors.birth_date')
                    .transform((value, originalValue) => {
                        originalValue = originalValue.split('/');
                        value = parse(
                            [originalValue[2], originalValue[1], originalValue[0]].join('-'),
                            'yyyy-mm-dd',
                            new Date(),
                        );
                        return value;
                    }),
            }),
        ),
});

export const formatTravelers = (
    travelers: Traveler[],
    fromFormat: string,
    toFormat: string,
): Traveler[] => {
    return travelers.map((traveler) => {
        const birthDate = traveler.birthDate.split(fromFormat);
        return {
            ...traveler,
            birthDate: [birthDate[2], birthDate[1], birthDate[0]].join(toFormat),
        };
    });
};

export const pricingValidationSchema = Yup.object().shape({
    totalPrice: Yup.number()
        .moreThan(0, 'contract.pricing.errors.required')
        .required('contract.pricing.errors.required'),
    pricePerPax: Yup.number(),
    internationalFlightsPrice: Yup.number().required('contract.pricing.errors.required'),
    domesticFlightsPrice: Yup.number().required('contract.pricing.errors.required'),
    landServicesPrice: Yup.number().positive('contract.pricing.errors.negative_land_services'),
    currency: Yup.string().required('contract.pricing.errors.currency_required'),
});

export const calculatePricePerPax = (price: number, pax: number): number => price / pax;

export function filterByFileName<T extends File | ProofDocument>(
    proofOfDocuments: T[],
    fileName: string,
): T[] {
    return proofOfDocuments.filter((document: T) => document.name !== fileName);
}

export function formatCurrenciesForSelectOptions(dossierMarketCode?: MarketCode): Option[] {
    const currenciesToUse = dossierMarketCode ? currenciesByMarket[dossierMarketCode] : currencies;

    return Object.values(currenciesToUse).map((currency) => ({
        label: currency.iso3,
        value: currency.iso3,
    }));
}

export function calculateTotalAmountOfInstalment(instalments: InstalmentViewModel[]): number {
    return instalments
        .filter((item) => item.price.amount !== '')
        .reduce((acc, item) => (item.price.amount as number) + acc, 0);
}

export const paymentScheduleValidationSchema = Yup.object().shape({
    currency: Yup.string().required('contract.pricing.errors.currency_required'),
    depositInstalments: Yup.array().of(
        Yup.object({
            payerName: Yup.string().required('contract.payment_schedule.errors.payer_name'),
            label: Yup.string().required('contract.payment_schedule.errors.label'),
            price: Yup.object({
                amount: Yup.number()
                    .required('contract.payment_schedule.errors.amount')
                    .positive('contract.payment_schedule.errors.amount'),
            }),
        }),
    ),
    balanceInstalments: Yup.array().of(
        Yup.object({
            payerName: Yup.string().required('contract.payment_schedule.errors.payer_name'),
            label: Yup.string().required('contract.payment_schedule.errors.label'),
            price: Yup.object({
                amount: Yup.number()
                    .required('contract.payment_schedule.errors.amount')
                    .positive('contract.payment_schedule.errors.amount'),
            }),
        }),
    ),
});

export const instalmentValidationSchema = Yup.object({
    payerName: Yup.string().required('contract.payment_schedule.errors.payer_name'),
    label: Yup.string().required('contract.payment_schedule.errors.label'),
    price: Yup.object({
        amount: Yup.number()
            .test(
                'is-decimal',
                'contract.payment_schedule.errors.amount',
                (value) => !!(value + '').match(/^\d+(\.\d{0,2})?$/),
            )
            .required('contract.payment_schedule.errors.amount')
            .positive('contract.payment_schedule.errors.amount'),
    }),
});
